import React, { useEffect, useState } from 'react';
import {
  TextFieldLabel, Select, Button, Spinner, Checkbox,
} from 'ui-component-library';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

import {
  FormHeader,
  InputContainer,
  SubmitButtonContainer,
  TopRowContainer,
  LoadingContainer,
  TextDescription,
  EmptyStateContainer,
} from './action-bounce-load.style';
import CommentBox from '../../../comments-box';
import loadShape from '../../../../../../shapes/load';
import EmailModal from '../../../modals/email-modal';
import { getBounceEmailBody } from '../../../modals/email-modal/email-templates';
import theme from '../../../../../../theme';
import {
  showSuccessMessage,
  showErrorMessage,
  validate,
  logger,
} from '../../../../../../utils/common';
import { bounceLoad, checkEmailServiceHealth } from '../../../../../../utils/api/load';
import { formatErrorResponse, createOption } from '../../../../../../utils/mappers';
import { getHtmlFromTxt, splitEmailString } from '../../../../../../utils/formatters';
import EmailErrorModal from '../../../modals/email-modal-error';
import { clearSelectedLoad } from '../../../../../../store/selected-load/slice';
import { clearUpdatedLoad } from '../../../../../../store/updated-load/slice';
import { COMMENTS_CHARACTER_LIMIT } from '../../../../../../constants';

// Validation Schema
export const schema = yup.object().shape({
  reason: yup.string().required(),
  isLiveBounce: yup.boolean().required(),
  note: yup.string().required().max(COMMENTS_CHARACTER_LIMIT, `Comment cannot be less than ${COMMENTS_CHARACTER_LIMIT}`),
});

/**
 * Bounce load action form
 * @param {object} params - Contains props for this component
 * @param {object} params.load - Load object to perform action on
 * @return {element} - The bounce load form
 */
const BounceLoadAction = ({ load }) => {
  // General hooks
  const location = useLocation();
  const history = useHistory();

  // State hooks
  const [selectedReason, setSelectedReason] = useState(null);
  const [comments, setComments] = useState(null);
  const [loadingEmail, setLoadingEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLiveBounce, setIsLiveBounce] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [emailFailed, setEmailFailed] = useState(false);

  // Redux hooks
  const dispatch = useDispatch();
  const user = useSelector((store) => store.oidc.user);
  const metadata = useSelector((state) => state.metadata);

  // Generate the email content that is used
  // when bouncing a load when this component renders
  const emailContent = {
    copyTo: user.emailaddress,
    recipient: '',
    subject: `${isLiveBounce ? 'LIVE BOUNCE #' : 'BOUNCE #'}${load?.billOfLadingNumber} ${load?.shipmentInformation?.origin?.address?.state}-${load?.shipmentInformation?.destination?.address?.state} ${load.customerName}`,
    body: getBounceEmailBody(load, comments, selectedReason?.value),
  };

  /**
   * Function to clear the current state of the form
   * @return {void}
   */
  const resetState = () => {
    setSelectedReason(null);
    setComments(null);
  };

  // If the load changes, reset the state
  // of this component
  useEffect(() => {
    resetState();
  }, [load]);

  /**
   * Checks if the form is valid
   * @return {boolean}
   */
  const checkValid = () => {
    const payload = {
      reason: selectedReason?.value,
      isLiveBounce,
      note: comments,
      salesOrderBk: load.billOfLadingNumber,
    };
    const valid = validate(payload, schema);
    return valid;
  };

  /**
   * Handles when the selected reason has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleSelectedReasonChanged = (event) => {
    setSelectedReason(event);
  };

  /**
   * Handles when the live bounce checkbox  has changed
   * @param {object} event - Change event
   * @return {void}
   */
  const handleIsLiveBounceChanged = () => {
    setIsLiveBounce(!isLiveBounce);
  };

  /**
   * Get reason options from metadata endpoint
   * @return {object[]} - Options array
   */
  const getReasonOptions = () => metadata?.data?.bounceReasons?.map(createOption);

  /**
   * Handles when comments change
   * @param {object} event - Change event
   * @return {void}
   */
  const handleCommentsChanged = (event) => {
    setComments(event.target.value);
  };

  /**
   * If bounce is successful close peekview/ redirect to tracking queue and remove load
   * @return {void}
   */
  const handleBounceSuccess = () => {
    // Remove bounced load from state
    dispatch(clearSelectedLoad());
    dispatch(clearUpdatedLoad());
    // bounce & email successful, navigate back to tracking queue or refresh page if on tracking queue
    if (location?.pathname.includes('/loads/detail')) {
      history.push('/');
    } else {
      history.push('/temp');
      history.replace('/');
    }
    showSuccessMessage(`Load #${load.billOfLadingNumber} has been successfully bounced. It can now be re-booked in TMS.`);
  };

  /**
   * Handles close for email error modal
   * @return {void}
   */
  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    // If email failed but load was bounced redirect on close
    if (emailFailed) {
      handleBounceSuccess();
    }
  };

  /**
   * Handles submit for bounce a load on action component
   * @return {void}
   */
  const handleSubmit = () => {
    const valid = checkValid();
    if (valid) {
      setLoading(true);
      // Check to see if email service is running
      checkEmailServiceHealth().then(() => {
        setShowEmailModal(true);
      }).catch(() => {
        setShowErrorModal(true);
      }).finally(() => {
        setLoading(false);
      });
    }
  };

  /**
   * Handles successful bounce
   * @return {void}
   */
  const bounceSuccessCallback = () => {
    // bounce & email successful, show toast and navigate back to tracking queue
    setLoadingEmail(false);
    handleBounceSuccess();
  };

  /**
   * Bounces a Load: submit for error modal
   * @return {void}
  */
  const handleBounceSubmit = () => {
    setLoadingEmail(true);
    // If email service is down email vars are null
    bounceLoad({
      reason: selectedReason.value,
      note: comments,
      isLiveBounce: true,
      loadId: load.loadId,
      salesOrderBk: load.billOfLadingNumber,
    }).then(bounceSuccessCallback).catch((error) => {
      logger.error(formatErrorResponse(error));
      // If health check has failed and email error modal bounce fails close the error modal
      setShowErrorModal(false);
      // Bounce failed
      showErrorMessage('Something went wrong. The bounce service is currently unavailable. Please try again later.');
      setLoadingEmail(false);
    });
  };

  /**
   * Bounces a Load and sends email: submit for email modal
   * @return {void}
  */
  const handleEmailBounceSubmit = (emailModalContent) => {
    setLoadingEmail(true);
    // If email service is down email vars are null
    bounceLoad({
      reason: selectedReason.value,
      note: comments,
      isLiveBounce: true,
      salesOrderBk: load.billOfLadingNumber,
      email: {
        to: splitEmailString(emailModalContent.recipient),
        copyTo: splitEmailString(emailModalContent.copyTo),
        subject: emailModalContent.subject,
        body: getHtmlFromTxt(emailModalContent.body),
        isBodyHtml: true,
        attachments: null,
      },
      loadId: load.loadId,
    }).then(bounceSuccessCallback).catch((error) => {
      logger.error(formatErrorResponse(error));
      // If bounce fails because email service is down or failed to send email
      if (error?.response?.data?.Message?.includes('email')) {
        setEmailFailed(true);
        setShowErrorModal(true);
      } else {
        // Bounce failed
        showErrorMessage('Something went wrong. The bounce service is currently unavailable. Please try again later.');
      }
      setShowEmailModal(false);
      setLoadingEmail(false);
    });
  };

  if (loading) {
    return (
      <div data-testid="Update Status Action">
        <FormHeader>Bounce Load</FormHeader>
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      </div>
    );
  }

  const valid = checkValid();
  const options = getReasonOptions();

  if (load.tlStatus === 'Delivered') {
    return (
      <EmptyStateContainer>
        This load cannot currently be bounced because it is in &quot;Delivered&quot; status.
      </EmptyStateContainer>
    );
  }

  return (
    <div data-testid="Bounce Load Action">
      <EmailErrorModal
        showModal={showErrorModal}
        title="Email Service Down"
        errorMessage={emailFailed ? 'The load was successfully bounced but the email did not send. Please copy the load information below and email it to the load manager. Then, close the pop-up.'
          : `Error: The email service is currently down. Please copy the load information below and email it to the load manager.
        Once you have copied the information, confirm the bounce. Bouncing the load will completely remove the load from Track & Trace until a new Carrier is assigned and booked in TMS.`}
        content={emailContent.body}
        cancelButtonLabel={emailFailed ? 'Close' : 'Cancel Bounce'}
        submitButtonLabel="Confirm"
        closeModal={handleCloseErrorModal}
        onEmailSubmit={handleBounceSubmit}
        emailFailed={emailFailed}
      />
      <EmailModal
        title="Email Bounce Information"
        showModal={showEmailModal}
        closeModal={() => setShowEmailModal(false)}
        emailContent={emailContent}
        onEmailSubmit={handleEmailBounceSubmit}
        isLoading={loadingEmail}
        cancelButtonLabel="Cancel Bounce"
        customerBk={load?.customerBk}
      />
      <FormHeader>Bounce Load</FormHeader>
      <TextDescription>
        Bouncing the Load will completely remove the load from Track &amp;
        Trace until a new Carrier is assigned and booked in TMS. In the next screen,
        you can review and send an email to inform your team.
      </TextDescription>
      <TopRowContainer>
        <InputContainer>
          <TextFieldLabel text="*Reason for Bounce" />
          <Select
            selectedValue={selectedReason}
            options={options}
            placeholder="Select a reason for bounce"
            onValueChange={handleSelectedReasonChanged}
          />
        </InputContainer>
        <Checkbox
          checked={isLiveBounce}
          onValueChange={handleIsLiveBounceChanged}
          name="isLiveBounceCheckbox"
          text="Live Bounce (Carrier may be re-assigned)"
          labelColor={theme.palette.black[100]}
        />
      </TopRowContainer>
      <CommentBox
        comment={comments}
        onCommentChanged={handleCommentsChanged}
        placeholder="Include helpful details related to the reason for bounce."
        characterLimit={COMMENTS_CHARACTER_LIMIT}
        required
      />
      <SubmitButtonContainer>
        <Button
          onClick={handleSubmit}
          text="Submit"
          isDisabled={!valid}
        />
      </SubmitButtonContainer>
    </div>
  );
};

BounceLoadAction.propTypes = {
  load: loadShape.isRequired,
};

export default BounceLoadAction;
